import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { usersAPI, authAPI } from '../../services/api';

/**
 * Admin Slice - Unified User Management
 * 
 * All users (including admins) are stored in the operator table.
 * User type is determined by their role:
 * - ADMIN: Full system access
 * - CONTENT_MANAGER: Content management access
 * - EDITOR: Content approval access
 * - CONTENT_WRITER: Content creation access
 * - VIEWER: Read-only access (no portal access)
 */

// Fetch all users (all operators from unified table)
export const fetchUsers = createAsyncThunk('admin/fetchUsers', async (params, { rejectWithValue }) => {
  try {
    const response = await usersAPI.getAll(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
  }
});

// Fetch users by role (e.g., only ADMIN users)
export const fetchUsersByRole = createAsyncThunk('admin/fetchUsersByRole', async ({ roleCode, params }, { rejectWithValue }) => {
  try {
    const response = await usersAPI.getByRole(roleCode, params);
    return { roleCode, data: response.data };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch users by role');
  }
});

// Fetch all roles
export const fetchRoles = createAsyncThunk('admin/fetchRoles', async (_, { rejectWithValue }) => {
  try {
    const response = await authAPI.getRoles();
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch roles');
  }
});

// Create user (operator with specified role)
export const createUser = createAsyncThunk('admin/createUser', async (data, { rejectWithValue }) => {
  try {
    const response = await usersAPI.create(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create user');
  }
});

// Update user
export const updateUser = createAsyncThunk('admin/updateUser', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await usersAPI.update(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update user');
  }
});

// Delete/deactivate user
export const deleteUser = createAsyncThunk('admin/deleteUser', async ({ id }, { rejectWithValue }) => {
  try {
    await usersAPI.delete(id);
    return { id };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
  }
});

// Change user password
export const changeUserPassword = createAsyncThunk('admin/changeUserPassword', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await usersAPI.changePassword(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to change password');
  }
});

const adminSlice = createSlice({
  name: 'admin',
  initialState: { 
    users: [],           // All users from operator table
    usersByRole: {},     // Users grouped by role { ADMIN: [], EDITOR: [], etc. }
    roles: [],           // Available roles
    loading: false, 
    error: null, 
    pagination: null,
    successMessage: null
  },
  reducers: { 
    clearError: (state) => { 
      state.error = null; 
    },
    clearSuccess: (state) => {
      state.successMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchUsers.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      
      // Fetch users by role
      .addCase(fetchUsersByRole.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(fetchUsersByRole.fulfilled, (state, action) => {
        state.loading = false;
        const { roleCode, data } = action.payload;
        state.usersByRole[roleCode] = data.data || [];
      })
      .addCase(fetchUsersByRole.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      
      // Fetch roles
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => { 
        state.loading = false;
        state.roles = action.payload || []; 
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create user
      .addCase(createUser.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(createUser.fulfilled, (state, action) => { 
        state.loading = false;
        if (action.payload) {
          state.users.unshift(action.payload);
          state.successMessage = 'User created successfully';
        }
      })
      .addCase(createUser.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.users.findIndex(u => u.id === action.payload.id);
          if (index !== -1) {
            state.users[index] = action.payload;
          }
          state.successMessage = 'User updated successfully';
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(u => u.id !== action.payload.id);
        state.successMessage = 'User deleted successfully';
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Change password
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Password changed successfully';
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = adminSlice.actions;
export default adminSlice.reducer;
